import React, { useEffect, useState, useContext } from "react";
import { DataGrid, GridFilterInputDate } from "@mui/x-data-grid";
import { Container, Typography, Box, CircularProgress, Alert, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, MenuItem, Select, InputLabel, FormControl, Autocomplete, InputAdornment } from "@mui/material";
import { API_ENDPOINTS } from "../config/api.config";
import { WORKING_DAYS_CONFIG } from "../config/local.config";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { UserContext } from '../components/MiniDrawer';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import { parseLocalDate } from '../utils/dateHelpers';
import { excludeForecastWorklogs } from '../utils/worklogFilters';

// Funzione per formattare la data in yyyy-mm-dd
const formatDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${month}-${day}`;
};

// Funzione per pulire il ticket_id rimuovendo spazi prima/dopo e tra prefix e resto
const cleanTicketId = (ticketId, prefix) => {
  if (!ticketId) return '';
  const trimmed = String(ticketId).trim();
  if (!prefix) return trimmed;
  const prefixTrimmed = String(prefix).trim();
  // Se il ticket_id inizia con il prefix, rimuovi gli spazi tra prefix e resto
  if (prefixTrimmed && trimmed.startsWith(prefixTrimmed)) {
    const afterPrefix = trimmed.slice(prefixTrimmed.length).trim();
    return prefixTrimmed + afterPrefix;
  }
  return trimmed;
};

// Funzioni per il controllo delle restrizioni temporali (stesse di DailyTracking)
function getWorkingDaysPassedInMonth(firstOfMonth, currentDate) {
  let workingDaysCount = 0;
  const currentDateNormalized = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
  
  // Itera attraverso tutti i giorni dall'inizio del mese fino ad oggi
  for (let day = 1; day <= currentDateNormalized.getDate(); day++) {
    const checkDate = new Date(currentDateNormalized.getFullYear(), currentDateNormalized.getMonth(), day);
    const dayOfWeek = checkDate.getDay();
    
    // Se il giorno è un giorno lavorativo, incrementa il contatore
    if (WORKING_DAYS_CONFIG.workingDaysOfWeek.includes(dayOfWeek)) {
      workingDaysCount++;
    }
  }
  
  return workingDaysCount;
}

function isWithinPreviousMonthEditingPeriod() {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  
  // Trova il primo giorno del mese corrente
  const firstOfMonth = new Date(currentYear, currentMonth, 1);
  
  // Calcola quanti giorni lavorativi sono passati dall'inizio del mese
  const workingDaysPassed = getWorkingDaysPassedInMonth(firstOfMonth, today);
  
  // Controlla se siamo entro il numero di giorni lavorativi configurati
  return workingDaysPassed <= WORKING_DAYS_CONFIG.previousMonthEditingDays;
}

function isExistingWorklogBlocked(worklogDate, projectId, projects) {
  if (!worklogDate) return true;
  const today = new Date();
  const sel = new Date(worklogDate.getFullYear(), worklogDate.getMonth(), worklogDate.getDate());
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  
  // Se la data del worklog è antecedente al mese precedente, blocca tutto
  if (sel < firstOfPreviousMonth) {
    return true;
  }
  
  // Se la data del worklog è del mese precedente
  if (sel < firstOfCurrentMonth) {
    // Se non siamo entro il periodo di editing configurato, blocca tutto
    if (!isWithinPreviousMonthEditingPeriod()) {
      return true;
    }
    
    // Se siamo entro il periodo di editing, controlla anche il campo previous_month_blocked del progetto
    if (projectId && projects) {
      const project = projects.find(p => p.id === projectId);
      if (project && project.previous_month_blocked === true) {
        return true; // Blocca se il progetto ha previous_month_blocked = true
      }
    }
    
    return false;
  }
  
  return false;
}

function getExistingWorklogRestrictionMessage(worklogDate, projectId, projects) {
  if (!worklogDate) return 'Invalid date';
  
  const today = new Date();
  const sel = new Date(worklogDate.getFullYear(), worklogDate.getMonth(), worklogDate.getDate());
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  
  // Se la data del worklog è antecedente al mese precedente
  if (sel < firstOfPreviousMonth) {
    return 'Worklog editing is not allowed for months before the previous month';
  }
  
  // Se la data del worklog è del mese precedente
  if (sel < firstOfCurrentMonth) {
    // Se non siamo entro il periodo di editing configurato
    if (!isWithinPreviousMonthEditingPeriod()) {
      return `Previous month editing is only allowed within the first ${WORKING_DAYS_CONFIG.previousMonthEditingDays} working day(s) of the current month`;
    }
    
    // Se siamo entro il periodo di editing, controlla anche il campo previous_month_blocked del progetto
    if (projectId && projects) {
      const project = projects.find(p => p.id === projectId);
      if (project && project.previous_month_blocked === true) {
        return 'Previous month editing is blocked for this project';
      }
    }
    
    // Se siamo entro il periodo di editing e il progetto non è bloccato, permette l'editing
    return 'Previous month editing is allowed';
  }
  
  return 'No restrictions';
}

// Funzione per verificare se una data è bloccata per un progetto specifico
function isDateBlockedForProject(date, projectId, projects) {
  if (!date || !projectId || !projects) return false;
  
  const project = projects.find(p => p.id === projectId);
  if (!project) return false;
  
  const selectedDate = new Date(date);
  const selectedNormalized = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
  const today = new Date();
  const todayNormalized = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  
  // Controllo 1: Data successiva alla end_date del progetto
  if (project.end_date) {
    const endDate = new Date(project.end_date);
    const endDateNormalized = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    if (selectedNormalized > endDateNormalized) return true;
  }
  
  // Controllo 2: Se la data è antecedente al mese precedente, blocca tutto
  if (selectedNormalized < firstOfPreviousMonth) {
    return true;
  }
  
  // Controllo 3: Se la data è del mese precedente
  if (selectedNormalized < firstOfCurrentMonth) {
    // Se non siamo entro il periodo di editing configurato, blocca tutto
    if (!isWithinPreviousMonthEditingPeriod()) return true;
    
    // Se siamo entro il periodo di editing, controlla il campo previous_month_blocked del progetto
    // Se previous_month_blocked è true, blocca l'editing per questo progetto
    if (project.previous_month_blocked === true) return true;
    
    // Se previous_month_blocked è false o null, controlla le restrizioni temporali per il mese precedente
    if (project.days_before_block !== null || project.days_after_block !== null) {
      // Calcola i limiti per il mese precedente
      let minAllowedDate = null;
      let maxAllowedDate = null;
      
      // Se days_before_block è null, non c'è limite per le date passate
      if (project.days_before_block !== null) {
        minAllowedDate = new Date(todayNormalized);
        minAllowedDate.setDate(todayNormalized.getDate() - project.days_before_block);
      }
      
      // Se days_after_block è null, non c'è limite per le date future
      if (project.days_after_block !== null) {
        maxAllowedDate = new Date(todayNormalized);
        maxAllowedDate.setDate(todayNormalized.getDate() + project.days_after_block);
      }
      
      // Verifica se la data è fuori dai limiti per il mese precedente
      if (minAllowedDate && selectedNormalized < minAllowedDate) return true;
      if (maxAllowedDate && selectedNormalized > maxAllowedDate) return true;
    }
    
    // Se arriviamo qui, il mese precedente è permesso
    return false;
  }
  
  // Controllo 4: Restrizioni temporali per il mese corrente (days_before_block e days_after_block)
  // Se non ci sono restrizioni temporali, non è bloccato
  if (project.days_before_block === null && project.days_after_block === null) return false;
  
  // Calcola i limiti
  let minAllowedDate = null;
  let maxAllowedDate = null;
  
  // Se days_before_block è null, non c'è limite per le date passate
  if (project.days_before_block !== null) {
    minAllowedDate = new Date(todayNormalized);
    minAllowedDate.setDate(todayNormalized.getDate() - project.days_before_block);
  }
  
  // Se days_after_block è null, non c'è limite per le date future
  if (project.days_after_block !== null) {
    maxAllowedDate = new Date(todayNormalized);
    maxAllowedDate.setDate(todayNormalized.getDate() + project.days_after_block);
  }
  
  // Verifica se la data è fuori dai limiti
  if (minAllowedDate && selectedNormalized < minAllowedDate) return true;
  if (maxAllowedDate && selectedNormalized > maxAllowedDate) return true;
  
  return false;
}

// Funzione per ottenere il messaggio di errore per le restrizioni temporali
function getDateRestrictionMessage(projectId, projects, worklogDate) {
  const project = projects.find(p => p.id === projectId);
  if (!project) return '';
  
  const today = new Date();
  const selectedNormalized = new Date(worklogDate.getFullYear(), worklogDate.getMonth(), worklogDate.getDate());
  const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const firstOfPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  
  // Formato dd-mm-yyyy
  const formatDateDDMMYYYY = (date) => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };
  
  // Controllo se la data selezionata è antecedente al mese precedente
  if (selectedNormalized < firstOfPreviousMonth) {
    return 'Worklog editing is not allowed for months before the previous month';
  }
  
  // Controllo se la data selezionata è del mese precedente
  if (selectedNormalized < firstOfCurrentMonth) {
    // Se non siamo entro il periodo di editing configurato
    if (!isWithinPreviousMonthEditingPeriod()) {
      return `Previous month creation and editing is only allowed within the first ${WORKING_DAYS_CONFIG.previousMonthEditingDays} working day(s) of the current month`;
    }
    
    // Se siamo entro il periodo di editing, controlla il campo previous_month_blocked del progetto
    if (project.previous_month_blocked === true) {
      return 'Previous month creation and editing is blocked for this project';
    }
    
    // Se previous_month_blocked è false o null, controlla le restrizioni temporali per il mese precedente
    if (project.days_before_block === null && project.days_after_block === null) {
      return 'Previous month creation and editing is allowed for this project';
    } else if (project.days_before_block === null) {
      // Solo limite futuro
      const maxAllowedDate = new Date(today);
      maxAllowedDate.setDate(today.getDate() + project.days_after_block);
      return `Allowed dates for this project: up to ${formatDateDDMMYYYY(maxAllowedDate)}`;
    } else if (project.days_after_block === null) {
      // Solo limite passato
      const minAllowedDate = new Date(today);
      minAllowedDate.setDate(today.getDate() - project.days_before_block);
      return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} onwards`;
    } else {
      // Entrambi i limiti
      const minAllowedDate = new Date(today);
      minAllowedDate.setDate(today.getDate() - project.days_before_block);
      const maxAllowedDate = new Date(today);
      maxAllowedDate.setDate(today.getDate() + project.days_after_block);
      return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} to ${formatDateDDMMYYYY(maxAllowedDate)}`;
    }
  }
  
  // Controllo end_date del progetto
  if (project.end_date) {
    const endDate = new Date(project.end_date);
    const endDateNormalized = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    
    // Se la data selezionata è dopo la end_date, mostra solo il messaggio di fine progetto
    if (selectedNormalized > endDateNormalized) {
      const endDateFormatted = formatDateDDMMYYYY(endDate);
      return `Project ended on ${endDateFormatted}`;
    }
  }
  
  // Se non c'è end_date o la data selezionata è prima della end_date, usa solo le restrizioni temporali
  if (project.days_before_block === null && project.days_after_block === null) {
    return 'No date restrictions for this project';
  } else if (project.days_before_block === null) {
    // Solo limite futuro
    const maxAllowedDate = new Date(today);
    maxAllowedDate.setDate(today.getDate() + project.days_after_block);
    return `Allowed dates for this project: up to ${formatDateDDMMYYYY(maxAllowedDate)}`;
  } else if (project.days_after_block === null) {
    // Solo limite passato
    const minAllowedDate = new Date(today);
    minAllowedDate.setDate(today.getDate() - project.days_before_block);
    return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} onwards`;
  } else {
    // Entrambi i limiti
    const minAllowedDate = new Date(today);
    minAllowedDate.setDate(today.getDate() - project.days_before_block);
    const maxAllowedDate = new Date(today);
    maxAllowedDate.setDate(today.getDate() + project.days_after_block);
    return `Allowed dates for this project: from ${formatDateDDMMYYYY(minAllowedDate)} to ${formatDateDDMMYYYY(maxAllowedDate)}`;
  }
}

function getColumnsFromData(data) {
  if (!Array.isArray(data) || data.length === 0) return [];
  // Colonne principali flat secondo il nuovo tracciato
  return [
    { field: 'project_name', headerName: 'Project', width: 180 },
    { field: 'ticket_id', headerName: 'Ticket ID', width: 140 },
    { 
      field: 'date', 
      headerName: 'Date', 
      width: 130,
      type: 'date',
      filterable: true,
      valueFormatter: (value) => {
        if (!value) return '';
        // Fix timezone: parseLocalDate interpreta la data come locale
        const date = parseLocalDate(value);
        return date.toLocaleDateString('en-GB'); // Format as DD/MM/YYYY
      },
      filterOperators: [
        {
          label: 'Equals',
          value: 'equals',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              return value === filterItem.value;
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'After',
          value: 'after',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              // Fix timezone: parseLocalDate interpreta la data come locale
              return parseLocalDate(value) > parseLocalDate(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
        {
          label: 'Before',
          value: 'before',
          getApplyFilterFn: (filterItem) => {
            if (!filterItem.value) {
              return null;
            }
            return ({ value }) => {
              if (!value) {
                return false;
              }
              // Fix timezone: parseLocalDate interpreta la data come locale
              return parseLocalDate(value) < parseLocalDate(filterItem.value);
            };
          },
          InputComponent: GridFilterInputDate,
        },
      ],
    },
    { 
      field: 'effort', 
      headerName: 'Effort (min)', 
      width: 100,
      valueFormatter: (value) => {
        if (!value) return '';
        const minutes = Number(value);
        return `${minutes}`;
      }
    },

    { field: 'activity_name', headerName: 'Activity', width: 180 },
    { field: 'effort_type_name', headerName: 'Effort Type', width: 140 },
    { field: 'description', headerName: 'Description', width: 250 },
  ];
}

// Funzione per normalizzare la data a solo giorno (ignora orario e fuso)
function normalizeDate(dateStr) {
  // Fix timezone: parseLocalDate interpreta la data come locale
  const d = parseLocalDate(dateStr);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

// Ripristino la funzione buildFormDataFromRow
function buildFormDataFromRow(row, users, projects, activities, effortTypes) {
  return {
    user: users.find(u => String(u.id) === String(row.user_id || row.user?.id)) || null,
    project: projects.find(p => String(p.id) === String(row.project_id || row.project?.id)) || null,
    activity: activities.find(a => String(a.id) === String(row.activity_id || row.activity?.id)) || null,
    effort_type: effortTypes.find(e => String(e.id) === String(row.effort_type_id || row.effort_type?.id)) || null,
    ticket_id: row.ticket_id || '',
    effort: row.effort ? `${Number(row.effort)}` : '',
    description: row.description || '',
    date: row.date ? row.date.slice(0, 10) : '',
    // status_id non editabile, default 1
  };
}

// Funzioni per parsing e formattazione effort (stesse di DailyTracking)
function parseEffortToMinutes(effortStr) {
  if (!effortStr || typeof effortStr !== 'string') return { minutes: 0, isValid: true, error: null };
  
  const input = effortStr.trim();
  if (input === '') return { minutes: 0, isValid: true, error: null };
  
  // Controlla se ci sono caratteri alfabetici non ammessi (diversi da m)
  const invalidChars = input.match(/[a-zA-Z]/g);
  if (invalidChars) {
    const invalidChar = invalidChars.find(char => char.toLowerCase() !== 'm');
    if (invalidChar) {
      return { 
        minutes: 0, 
        isValid: false, 
        error: `Invalid character '${invalidChar}'. Only numbers are allowed.` 
      };
    }
  }
  
  // Pattern per solo numeri (es. "20", "45", "120") - trattati come minuti
  const numberOnlyPattern = /^(\d+)$/;
  const numberOnlyMatch = input.match(numberOnlyPattern);
  if (numberOnlyMatch) {
    const minutes = parseInt(numberOnlyMatch[1]);
    if (minutes > 1440) { // 24 ore in minuti
      return { 
        minutes: 0, 
        isValid: false, 
        error: 'Minutes cannot exceed 1440 (24 hours)' 
      };
    }
    return { minutes, isValid: true, error: null };
  }
  
  // Pattern per numeri seguiti da 'm' (es. "20m", "45m") - retrocompatibilità
  const minutePattern = /^(\d+)m$/i;
  const minuteMatch = input.match(minutePattern);
  if (minuteMatch) {
    const minutes = parseInt(minuteMatch[1]);
    if (minutes > 1440) { // 24 ore in minuti
      return { 
        minutes: 0, 
        isValid: false, 
        error: 'Minutes cannot exceed 1440 (24 hours)' 
      };
    }
    return { minutes, isValid: true, error: null };
  }
  
  // Se non matcha nessun pattern valido
  return { 
    minutes: 0, 
    isValid: false, 
    error: 'Invalid format. Enter minutes as a number (e.g., 30, 60, 120)' 
  };
}


// Funzione per filtrare i progetti in base ai criteri richiesti
const filterProjectsForUser = async (currentUser, selectedDate) => {
  if (!currentUser || !selectedDate) {
    return [];
  }
  
  try {
    // Ottieni direttamente i progetti dell'utente usando l'endpoint specifico
    const projectUsersUrl = `${API_ENDPOINTS.PROJECT_USERS}?user_id=${currentUser.id}`;
    
    const projectUsersResponse = await fetch(projectUsersUrl);
    
    if (!projectUsersResponse.ok) {
      console.error('Project-users API error:', projectUsersResponse.status);
      return [];
    }
    
    const projectUsersData = await projectUsersResponse.json();
    
    // Estrai i progetti dalla struttura corretta mantenendo anche validity_end_date
    const userProjectsWithReferences = projectUsersData.user_projects_with_references || [];
    const userProjects = userProjectsWithReferences.map(item => ({
      ...item.project,
      customer: item.customer,
      validity_end_date: item.validity_end_date, // Mantieni il campo validity_end_date
      ticket_id_prefix: item.project.ticket_id_prefix // Mantieni il campo ticket_id_prefix
    }));
    
    // Filtra i progetti per data di validità
    const workDate = formatDate(selectedDate);
    const filteredProjects = userProjects.filter(project => {
      // 1. Il progetto deve essere attivo
      if (!project.active) {
        return false;
      }
      
      // 2. La data di validità deve ricadere nel giorno selezionato
      const projectStartDate = project.start_date;
      const projectEndDate = project.end_date;
      
      // Se il progetto ha date di validità, verifica che la data di lavoro rientri nel range
      if (projectStartDate && projectEndDate) {
        const isValidDate = workDate >= projectStartDate && workDate <= projectEndDate;
        if (!isValidDate) {
          return false;
        }
      }
      
      // 3. Controlla la validity_end_date dell'utente per questo progetto
      if (project.validity_end_date) {
        // Fix timezone: parseLocalDate interpreta la data come locale
        const validityEndDate = parseLocalDate(project.validity_end_date);
        const selectedDateObj = new Date(selectedDate);
        
        // Normalizza le date per confrontare solo giorno/mese/anno
        const validityEndNormalized = new Date(validityEndDate.getFullYear(), validityEndDate.getMonth(), validityEndDate.getDate());
        const selectedNormalized = new Date(selectedDateObj.getFullYear(), selectedDateObj.getMonth(), selectedDateObj.getDate());
        
        // Se la data selezionata è successiva alla validity_end_date, escludi il progetto
        if (selectedNormalized > validityEndNormalized) {
          return false;
        }
      }
      
      return true;
    });
    
    return filteredProjects;
  } catch (error) {
    console.error('Error filtering projects:', error);
    return [];
  }
};

export default function TrackingListUserView() {
  const { selectedUser, backendUser } = useContext(UserContext);
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  // 2. Rimuovo la funzione buildFormDataFromRow (non più usata)
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const [statusList, setStatusList] = useState([]);
  const [effortTypes, setEffortTypes] = useState([]);
  const [allowedActivitiesByProject, setAllowedActivitiesByProject] = useState([]);

  // Stato per il dialog di update/insert
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    user: '',
    project: '',
    activity: '',
    effort_type: '',
    effort: '',
    ticket_id: '',
    description: '',
  });
  const [formError, setFormError] = useState(null);
  const [saving, setSaving] = useState(false);
  

  // Filtri per progetto e intervallo date
  const [filterProject, setFilterProject] = useState('');
  // Imposta di default il mese corrente
  function getCurrentMonthRange() {
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDayOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    // yyyy-mm-dd
    const toStr = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: toStr(firstDayOfMonth), end: toStr(lastDayOfMonth) };
  }
  const currentMonth = getCurrentMonthRange();
  const [filterStartDate, setFilterStartDate] = useState(currentMonth.start);
  const [filterEndDate, setFilterEndDate] = useState(currentMonth.end);

  // Stato per i filtri "pending" (modificabili dall'utente)
  const [pendingFilterProject, setPendingFilterProject] = useState(filterProject);
  const [pendingFilterStartDate, setPendingFilterStartDate] = useState(filterStartDate);
  const [pendingFilterEndDate, setPendingFilterEndDate] = useState(filterEndDate);

  // Aggiorna i filtri effettivi solo quando si preme il pulsante
  const applyFilters = () => {
    setFilterProject(pendingFilterProject);
    setFilterStartDate(pendingFilterStartDate);
    setFilterEndDate(pendingFilterEndDate);
  };

  // Carica i progetti associati all'utente loggato per il filtro principale
  useEffect(() => {
    const loadUserProjects = async () => {
      if (backendUser) {
        try {
          const projectUsersUrl = `${API_ENDPOINTS.PROJECT_USERS}?user_id=${backendUser.id}`;
          const projectUsersResponse = await fetch(projectUsersUrl);
          
          if (!projectUsersResponse.ok) {
            console.error('Project-users API error:', projectUsersResponse.status);
            setProjects([]);
            return;
          }
          
          const projectUsersData = await projectUsersResponse.json();
          
          // Estrai i progetti dalla struttura corretta
          const userProjectsWithReferences = projectUsersData.user_projects_with_references || [];
          const userProjects = userProjectsWithReferences.map(item => ({
            ...item.project,
            customer: item.customer,
            ticket_id_prefix: item.project.ticket_id_prefix
          }));
          
          setProjects(userProjects);
        } catch (error) {
          console.error('Error loading user projects:', error);
          setProjects([]);
        }
      } else {
        setProjects([]);
      }
    };
    
    loadUserProjects();
  }, [backendUser]);

  // Carica tutti gli utenti all'avvio
  useEffect(() => {
    fetch(API_ENDPOINTS.USERS)
      .then(res => res.json())
      .then(data => {
        let arr = data.users || [];
        setUsers(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setUsers([]));
  }, []);

  // Aggiorna formData quando users è popolato e c'è una riga in editing
  useEffect(() => {
    // 2. Rimuovo lo stato e le funzioni: openDialog, editingRow, formData, handleOpenDialog, handleCloseDialog, handleFormChange, handleSave
    // 3. Rimuovo la colonna Edit (matita) dalla tabella (actionsColumn, IconButton/EditIcon, ecc.)
    // 4. Rimuovo il Dialog (Dialog, DialogTitle, DialogContent, DialogActions) dal render
  }, [users]);

  // Aggiorna il valore selezionato nei select quando la lista cambia e il dialog è aperto
  useEffect(() => {
    if (openDialog && editingRow) {
      setFormData(prev => ({
        ...prev,
        user: users.find(u => String(u.id) === String(editingRow.user_id))?.id || prev.user,
      }));
    }
  }, [users, openDialog, editingRow]);
  useEffect(() => {
    if (openDialog && editingRow) {
      setFormData(prev => ({
        ...prev,
        project: projects.find(p => String(p.id) === String(editingRow.project_id))?.id || prev.project,
      }));
    }
  }, [projects, openDialog, editingRow]);
  useEffect(() => {
    if (openDialog && editingRow) {
      setFormData(prev => ({
        ...prev,
        activity: activities.find(a => String(a.id) === String(editingRow.activity_id))?.id || prev.activity,
      }));
    }
  }, [activities, openDialog, editingRow]);
  useEffect(() => {
    if (openDialog && editingRow) {
      setFormData(prev => ({
        ...prev,
        effort_type: effortTypes.find(e => String(e.id) === String(editingRow.effort_type_id))?.id || prev.effort_type,
      }));
    }
  }, [effortTypes, openDialog, editingRow]);

  // Sposto fetchWorklogs fuori da useEffect
  const fetchWorklogs = async () => {
    setLoading(true);
    setError(null);
    try {
      // Costruisci la query string con i filtri data e user_id
      const params = [];
      if (filterStartDate) params.push(`date_from=${encodeURIComponent(filterStartDate)}`);
      if (filterEndDate) params.push(`date_to=${encodeURIComponent(filterEndDate)}`);
      // Filtra sempre per l'utente selezionato (user view)
      if (selectedUser && selectedUser !== 'ALL') {
        params.push(`user_id=${encodeURIComponent(selectedUser)}`);
      }
      let url = API_ENDPOINTS.WORKLOGS;
      if (params.length > 0) {
        url += (url.includes('?') ? '&' : '?') + params.join('&');
      }
      console.log('TrackingListUserView API query:', url); // <-- LOG DELLA QUERY
      const response = await fetch(url);
      if (!response.ok) throw new Error("Error fetching data");
      const data = await response.json();
      const arr = excludeForecastWorklogs(data.worklogs || []);
      console.log('Dati ricevuti:', arr);
      setRows(arr.map((row, idx) => ({ id: row.id || idx, ...row })));
      setColumns(getColumnsFromData(arr));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Aggiorno la useEffect che richiama fetchWorklogs per dipendere solo dai filtri effettivi
  useEffect(() => {
    if (!selectedUser) return;
    fetchWorklogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUser, filterProject, filterStartDate, filterEndDate]);

  // Sostituisco il filtro rows con log dettagliato
  const filteredRows = rows.filter(row => {
    let match = true;
    if (filterProject) {
      if (row.project_name !== filterProject) {
        console.log('Riga esclusa per project:', row);
        match = false;
      }
    }
    if (filterStartDate) {
      if (!(row.date && normalizeDate(row.date) >= normalizeDate(filterStartDate))) {
        console.log('Riga esclusa per start date:', row);
        match = false;
      }
    }
    if (filterEndDate) {
      if (!(row.date && normalizeDate(row.date) <= normalizeDate(filterEndDate))) {
        console.log('Riga esclusa per end date:', row);
        match = false;
      }
    }
    return match;
  });
  console.log('Filtri attivi:', filterProject, filterStartDate, filterEndDate);
  console.log('Righe mostrate:', filteredRows);

  // Ricava i campi editabili (escludi id, created_at, updated_at)
  const getEditableFields = () => {
    if (!columns.length) return [];
    return columns.filter(col => !['id', 'created_at', 'updated_at'].includes(col.field));
  };

  const fetchAllowedActivityIdsForProject = async (projectId) => {
    if (!projectId) return [];
    try {
      const resp = await fetch(`${API_ENDPOINTS.PROJECT_ACTIVITIES}?project_id=${projectId}`);
      if (!resp.ok) return [];
      const data = await resp.json();
      const ids = Array.isArray(data.activity_ids) ? data.activity_ids : [];
      return ids;
    } catch {
      return [];
    }
  };

  // Funzione per aprire il dialog di update
  const handleEditDialog = (row) => {
    setFormData({
      id: row.id || '',
      project: row.project_id || '',
      activity: row.activity_id || '',
      effort_type: row.effort_type_id || '',
      effort: row.effort ? `${Number(row.effort)}` : '',
      ticket_id: row.ticket_id || '',
      description: row.description || '',
    });
    setEditingRow(row);
    setFormError(null);
    setOpenDialog(true);
    
    
    // Carica i progetti filtrati per l'utente
    const loadFilteredProjects = async () => {
      if (backendUser) {
        const filteredProjects = await filterProjectsForUser(backendUser, new Date(row.date));
        setProjects(filteredProjects);
      }
    };
    loadFilteredProjects();
    
    // Fetch activities and allowed activities for the project
    fetch(API_ENDPOINTS.ACTIVITIES)
      .then(res => res.json())
      .then(data => {
        let arr = data.activities || [];
        setActivities(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setActivities([]));
    
    if (row.project_id) {
      fetchAllowedActivityIdsForProject(row.project_id).then(ids => {
        setAllowedActivitiesByProject(ids);
      });
    } else {
      setAllowedActivitiesByProject([]);
    }
    
    // Fetch effort types
    fetch(API_ENDPOINTS.EFFORT_TYPES)
      .then(res => res.json())
      .then(data => {
        let arr = data.effort_types || [];
        setEffortTypes(Array.isArray(arr) ? arr : []);
      })
      .catch(() => setEffortTypes([]));
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRow(null);
    setFormError(null);
  };
  // Funzione per gestire la prevalorizzazione del ticket_id
  const handleTicketIdPrefix = (projectId, activityId) => {
    if (!projectId || !activityId) return '';
    
    // Trova il progetto selezionato
    const selectedProject = projects.find(p => p.id === projectId);
    if (!selectedProject || !selectedProject.ticket_id_prefix) return '';
    
    // Trova l'attività selezionata
    const selectedActivity = activities.find(a => a.id === activityId);
    if (!selectedActivity || !selectedActivity.requires_ticket_id) return '';
    
    // Controlla se l'attività è "ticket resolution" (AM o ENH)
    const activityName = selectedActivity.name.toLowerCase();
    const isTicketResolution = activityName.includes('ticket resolution') || 
                              activityName.includes('am') || 
                              activityName.includes('enh');
    
    if (isTicketResolution) {
      return selectedProject.ticket_id_prefix;
    }
    
    return '';
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };
  // Salva update
  const handleSaveWorklog = async () => {
    setSaving(true);
    setFormError(null);
    
    // Trova l'attività selezionata per verificare se richiede ticket_id/description
    const selectedActivity = activities.find(a => a.id === formData.activity);
    const requiresTicketId = selectedActivity?.requires_ticket_id;
    const requiresDescription = selectedActivity?.requires_description;
    
    // Validazione campi obbligatori
    const requiredFields = ['project', 'activity', 'effort_type', 'effort'];
    if (requiresTicketId) {
      requiredFields.push('ticket_id');
    }
    if (requiresDescription) {
      requiredFields.push('description');
    }
    
    const missingFields = requiredFields.filter(field => !formData[field]);
    if (missingFields.length > 0) {
      const fieldNames = missingFields.map(field => field.replace('_', ' ').toUpperCase());
      setFormError(`Please fill in all required fields: ${fieldNames.join(', ')}`);
      setSaving(false);
      return;
    }
    
    // Converti effort da formato ore:minuti a minuti
    const effortResult = parseEffortToMinutes(formData.effort);
    if (!effortResult.isValid) {
      setFormError(effortResult.error);
      setSaving(false);
      return;
    }
    
    const effortMinutes = effortResult.minutes;

    // Validazione regex su ticket_id se prevista a livello progetto (considera l'eventuale prefix bloccato)
    const selectedProjectForRegex = projects.find(p => p.id === formData.project);
    if (requiresTicketId && selectedProjectForRegex?.regex) {
      try {
        let regexStr = String(selectedProjectForRegex.regex || '');
        let flags = '';
        if (regexStr.startsWith('/') && regexStr.lastIndexOf('/') > 0) {
          const lastSlash = regexStr.lastIndexOf('/');
          flags = regexStr.slice(lastSlash + 1);
          regexStr = regexStr.slice(1, lastSlash);
        }
        const pattern = new RegExp(regexStr, flags);
        const prefix = (selectedProjectForRegex.ticket_id_prefix || '').trim();
        const raw = (formData.ticket_id || '').trim();
        const toValidate = prefix && raw.startsWith(prefix) ? raw.slice(prefix.length).trim() : raw;
        console.debug('[TICKET_ID VALIDATION][UserView] single', { regexStr, flags, prefix, raw, toValidate, requiresTicketId });
        const match = pattern.test(toValidate);
        console.debug('[TICKET_ID VALIDATION][UserView] single result', { match });
        if (!match) {
          setFormError('Ticket ID format not valid for this project');
          setSaving(false);
          return;
        }
      } catch (e) {
        console.warn('Invalid project regex:', selectedProjectForRegex.regex, e);
      }
    }

    // (rimosso: duplicato)
    
    // Validazione: effort deve essere almeno 1 minuto
    if (effortMinutes < 1) {
      setFormError('Effort must be at least 1 minute');
      setSaving(false);
      return;
    }
    if (formData.description && formData.description.length > 400) {
      setFormError('Description cannot exceed 400 characters');
      setSaving(false);
      return;
    }
    if (requiresDescription && (!formData.description || String(formData.description).trim() === '')) {
      setFormError('Description is required for this activity');
      setSaving(false);
      return;
    }
    try {
      const payload = {
        id: editingRow ? editingRow.id : '',
        project_id: formData.project,
        activity_id: formData.activity,
        status_id: 1,
        effort_type_id: formData.effort_type,
        effort: effortMinutes,
        ticket_id: cleanTicketId(formData.ticket_id, selectedProjectForRegex?.ticket_id_prefix),
        description: formData.description,
        date: editingRow ? (editingRow.date ? editingRow.date.slice(0, 10) : '') : '',
      };
      
      if (editingRow) {
        const response = await fetch(`${API_ENDPOINTS.WORKLOGS}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const err = await response.text();
          throw new Error(err);
        }
        
        setOpenDialog(false);
        setEditingRow(null);
        // Ricarica worklog
        fetchWorklogs();
      }
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Elimina
  const handleDelete = async (id) => {
    // Trova il worklog da eliminare per controllare le restrizioni
    const worklogToDelete = rows.find(row => row.id === id);
    if (worklogToDelete) {
      const worklogDate = worklogToDelete.date ? new Date(worklogToDelete.date) : null;
      const projectId = worklogToDelete.project_id || worklogToDelete.project?.id;
      
      // Controlla se l'eliminazione è bloccata (stessa logica del bottone + worklog)
      if (worklogDate && isExistingWorklogBlocked(worklogDate, projectId, projects)) {
        const restrictionMessage = getExistingWorklogRestrictionMessage(worklogDate, projectId, projects);
        alert(`Cannot delete worklog: ${restrictionMessage}`);
        return;
      }
    }
    
    if (!window.confirm('Are you sure you want to delete this row?')) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_ENDPOINTS.WORKLOGS}/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Error deleting');
      await fetchWorklogs();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Aggiungi azioni alla tabella
  // Sposta 'Actions' in prima posizione e 'Status' in ultima
  const statusCol = columns.find(col => col.field === 'status_name');
  const otherCols = columns.filter(col => col.field !== 'status_name');
  const actionsColumn = {
    field: 'actions',
    headerName: 'Actions',
    width: 120,
    renderCell: (params) => {
      // Calcola se la data è antecedente al mese corrente
      const today = new Date();
      const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const worklogDate = params.row.date ? new Date(params.row.date) : null;
      const projectId = params.row.project_id || params.row.project?.id;
      
      // Usa la stessa logica del bottone + worklog per determinare se è bloccato
      const isOld = worklogDate && isExistingWorklogBlocked(worklogDate, projectId, projects);
      const isBlocked = params.row.status_name === 'Approved' || isOld;
      return (
        <Box>
          {isBlocked ? (
            <Tooltip title={isOld ? getExistingWorklogRestrictionMessage(worklogDate, projectId, projects) : "Blocked"}>
              <span>
                <IconButton disabled style={{ color: '#bdbdbd' }}>
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Edit">
              <span>
                <IconButton onClick={() => handleEditDialog(params.row)}>
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isBlocked ? (
            <Tooltip title={isOld ? getExistingWorklogRestrictionMessage(worklogDate, projectId, projects) : "Blocked"}>
              <span>
                <IconButton disabled style={{ color: '#bdbdbd' }}>
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Tooltip title="Delete">
              <span>
                <IconButton onClick={() => handleDelete(params.row.id)}>
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      );
    }
  };
  const columnsWithActions = [actionsColumn, ...columns.filter(col => col.field !== 'status_name')];

  // Tooltip per la colonna Description
  const columnsWithDescriptionTooltip = columnsWithActions.map(col => {
    if (col.field === 'description') {
      return {
        ...col,
        renderCell: (params) => (
          <Tooltip title={params.value || ''} placement="top" arrow>
            <span style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
              width: '100%'
            }}>{params.value}</span>
          </Tooltip>
        )
      };
    }
    return col;
  });

  // Funzione per esportare la tabella in Excel
  const handleExportExcel = () => {
    if (!filteredRows || filteredRows.length === 0) return;
    // Escludi la colonna delle azioni
    const exportColumns = columnsWithActions.filter(col => col.field !== 'actions');
    const dataToExport = filteredRows.map(row => {
      const obj = {};
      exportColumns.forEach(col => {
        // Cambia il nome della colonna effort per l'export
        if (col.field === 'effort') {
          obj['Effort in minutes'] = row[col.field];
        } else if (col.field === 'date') {
          // Formatta la data per Excel (YYYY-MM-DD) evitando problemi di timezone
          const dateValue = row[col.field];
          if (dateValue) {
            let formattedDate, year, month;
            
            // Usa la data direttamente se è già in formato YYYY-MM-DD
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
              formattedDate = dateValue;
              [year, month] = dateValue.split('-');
            } else {
              // Altrimenti converte evitando problemi di timezone
              const date = new Date(dateValue);
              year = date.getFullYear();
              month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              formattedDate = `${year}-${month}-${day}`;
            }
            
            obj[col.headerName || col.field] = formattedDate;
            // Aggiungi colonne Month e Year
            obj['Month'] = month;
            obj['Year'] = year;
          } else {
            obj[col.headerName || col.field] = '';
            obj['Month'] = '';
            obj['Year'] = '';
          }
        } else {
          obj[col.headerName || col.field] = row[col.field];
        }
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Worklogs');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    
    // Genera il nome del file con l'intervallo di date e progetto
    let fileName = 'worklogs';
    
    // Funzione per convertire YYYY-MM-DD in DDMMYYYY
    const formatDateForFileName = (dateStr) => {
      if (!dateStr) return '';
      const [year, month, day] = dateStr.split('-');
      return `${day}${month}${year}`;
    };
    
    // Funzione per pulire il nome del progetto per il nome del file
    const cleanProjectName = (projectName) => {
      if (!projectName) return '';
      return projectName.replace(/[^a-zA-Z0-9]/g, '_'); // Sostituisce caratteri speciali con underscore
    };
    
    const projectName = filterProject ? cleanProjectName(filterProject) : '';
    
    if (filterStartDate && filterEndDate) {
      const startFormatted = formatDateForFileName(filterStartDate);
      const endFormatted = formatDateForFileName(filterEndDate);
      if (projectName) {
        fileName = `worklogs_${projectName}_${startFormatted}-${endFormatted}`;
      } else {
        fileName = `Worklogs_${startFormatted}-${endFormatted}`;
      }
    } else if (filterStartDate) {
      const startFormatted = formatDateForFileName(filterStartDate);
      if (projectName) {
        fileName = `worklogs_${projectName}_from_${startFormatted}`;
      } else {
        fileName = `Worklogs_from_${startFormatted}`;
      }
    } else if (filterEndDate) {
      const endFormatted = formatDateForFileName(filterEndDate);
      if (projectName) {
        fileName = `worklogs_${projectName}_until_${endFormatted}`;
      } else {
        fileName = `Worklogs_until_${endFormatted}`;
      }
    } else if (projectName) {
      fileName = `worklogs_${projectName}`;
    }
    
    saveAs(blob, `${fileName}.xlsx`);
  };

  return (
    <Box>
      <Box my={4}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h4" gutterBottom sx={{ fontSize: '1.5rem' }}>All Tracking List</Typography>
          <Button variant="outlined" color="primary" onClick={handleExportExcel} startIcon={<DownloadIcon />}>Excel</Button>
        </Box>
        {/* Filtri progetto e intervallo date */}
        <Box 
          display="flex"
          flexDirection={{ xs: 'column', md: 'row' }}
          gap={2}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent={{ xs: 'flex-start', md: 'flex-start' }}
          mb={2}
        >
          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="filter-project-label">Project</InputLabel>
            <Select
              labelId="filter-project-label"
              value={pendingFilterProject}
              label="Project"
              onChange={e => setPendingFilterProject(e.target.value)}
              renderValue={(value) => value}
            >
              <MenuItem value="">All</MenuItem>
              {projects.map(p => (
                <MenuItem key={p.id} value={p.name}>
                  <Box>
                    <Typography variant="body2">{p.name}</Typography>
                    {p.customer?.name && (
                      <Typography variant="caption" color="textSecondary">
                        {p.customer.name}
                      </Typography>
                    )}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={pendingFilterStartDate}
            onChange={e => setPendingFilterStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={pendingFilterEndDate}
            onChange={e => setPendingFilterEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" size="small" color="primary" onClick={applyFilters} startIcon={<FilterListIcon />}>Search</Button>
        </Box>
        {loading ? (
          <CircularProgress />
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ height: 600, width: '100%' }}>
            <DataGrid
              rows={filteredRows}
              columns={columnsWithDescriptionTooltip}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[5, 10, 25, 50, 100]}
              pagination
              hideFooter={false}
              sx={{ bgcolor: 'white' }}
              filterMode="client"
              disableColumnFilter={false}
              disableColumnMenu={false}
              helperText={(() => {
                const selectedProject = projects.find(p => p.id === formData.project);
                const selectedActivity = activities.find(a => a.id === formData.activity);
                if (!selectedActivity) return '';
                const activityName = selectedActivity.name?.toLowerCase?.() || '';
                const isTicketResolution = activityName.includes('ticket resolution') || activityName.includes('am') || activityName.includes('enh');
                const parts = [];
                if (selectedProject?.ticket_id_prefix && isTicketResolution) {
                  parts.push(`Prefix "${selectedProject.ticket_id_prefix}" is locked.`);
                }
                if (isTicketResolution && (selectedProject?.regex || selectedProject?.regex_desc)) {
                  if (selectedProject?.regex_desc) {
                    parts.push(selectedProject.regex_desc);
                  } else {
                    parts.push('This project enforces a specific Ticket ID format.');
                  }
                }
                return parts.join(' ');
              })()}
            />
          </Box>
        )}
      </Box>
      {/* Dialog per creazione/modifica */}
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogTitle>{editingRow ? 'Update Worklog' : 'New Worklog'}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                         <Autocomplete
               options={projects}
               getOptionLabel={(option) => {
                 return `${option.name}${option.customer?.name ? ` - ${option.customer.name}` : ''}`;
               }}
               value={projects.find(p => p.id === formData.project) || null}
              onChange={(event, newValue) => {
                const newProjectId = newValue ? newValue.id : '';
                const ticketIdPrefix = handleTicketIdPrefix(newProjectId, formData.activity);
                handleFormChange({
                  target: {
                    name: 'project',
                    value: newProjectId
                  }
                });
                if (ticketIdPrefix) {
                  handleFormChange({
                    target: {
                      name: 'ticket_id',
                      value: ticketIdPrefix
                    }
                  });
                }
                (async () => {
                  const ids = await fetchAllowedActivityIdsForProject(newProjectId);
                  setAllowedActivitiesByProject(ids);
                  if (ids.length && formData.activity && !ids.includes(formData.activity)) {
                    setFormData(prev => ({ ...prev, activity: '' }));
                  }
                })();
              }}
               renderInput={(params) => (
                 <TextField
                   {...params}
                   label="Project"
                   required
                   name="project"
                 />
               )}
               renderOption={(props, option) => (
                 <Box component="li" {...props}>
                   <Box>
                     <Typography 
                       variant="body2" 
                       sx={{ 
                         color: 'text.primary'
                       }}
                     >
                       {option.name}
                     </Typography>
                     {option.customer?.name && (
                       <Typography variant="caption" color="textSecondary">
                         {option.customer.name}
                       </Typography>
                     )}
                   </Box>
                 </Box>
               )}
               isOptionEqualToValue={(option, value) => option.id === value.id}
               sx={{ mb: 2 }}
             />
            <Autocomplete
              options={activities}
              getOptionLabel={(option) => option.name || ''}
              value={activities.find(a => a.id === formData.activity) || null}
              onChange={(event, newValue) => {
                const newActivityId = newValue ? newValue.id : '';
                const ticketIdPrefix = handleTicketIdPrefix(formData.project, newActivityId);
                handleFormChange({
                  target: {
                    name: 'activity',
                    value: newActivityId
                  }
                });
                if (ticketIdPrefix) {
                  handleFormChange({
                    target: {
                      name: 'ticket_id',
                      value: ticketIdPrefix
                    }
                  });
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Activity"
                  required
                  name="activity"
                />
              )}
              filterOptions={(opts) => {
                if (!formData.project || !Array.isArray(allowedActivitiesByProject) || allowedActivitiesByProject.length === 0) return opts;
                return opts.filter(o => allowedActivitiesByProject.includes(o.id));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2 }}
            />
            <Autocomplete
              options={effortTypes}
              getOptionLabel={(option) => option.name || ''}
              value={effortTypes.find(et => et.id === formData.effort_type) || null}
              onChange={(event, newValue) => {
                handleFormChange({
                  target: {
                    name: 'effort_type',
                    value: newValue ? newValue.id : ''
                  }
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Effort Type"
                  required
                  name="effort_type"
                />
              )}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ mb: 2 }}
            />
                         <TextField
               name="ticket_id"
               label="Ticket"
               value={formData.ticket_id}
               onChange={(e) => {
                 const selectedProject = projects.find(p => p.id === formData.project);
                 const selectedActivity = activities.find(a => a.id === formData.activity);
                 
                 if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                   const activityName = selectedActivity.name.toLowerCase();
                   const isTicketResolution = activityName.includes('ticket resolution') || 
                                             activityName.includes('am') || 
                                             activityName.includes('enh');
                   
                   if (isTicketResolution) {
                     const prefix = selectedProject.ticket_id_prefix;
                     const newValue = e.target.value;
                     
                     // Se il nuovo valore non inizia con il prefix, mantieni il prefix
                     if (!newValue.startsWith(prefix)) {
                       e.target.value = prefix;
                     }
                   }
                 }
                 
                 handleFormChange(e);
               }}
               onKeyDown={(e) => {
                 const selectedProject = projects.find(p => p.id === formData.project);
                 const selectedActivity = activities.find(a => a.id === formData.activity);
                 
                 if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                   const activityName = selectedActivity.name.toLowerCase();
                   const isTicketResolution = activityName.includes('ticket resolution') || 
                                             activityName.includes('am') || 
                                             activityName.includes('enh');
                   
                   if (isTicketResolution) {
                     const prefix = selectedProject.ticket_id_prefix;
                     const currentValue = e.target.value;
                     const cursorPosition = e.target.selectionStart;
                     
                     // Se l'utente prova a cancellare il prefix (Backspace o Delete)
                     if ((e.key === 'Backspace' || e.key === 'Delete') && cursorPosition < prefix.length) {
                       e.preventDefault();
                     }
                   }
                 }
               }}
               onSelect={(e) => {
                 const selectedProject = projects.find(p => p.id === formData.project);
                 const selectedActivity = activities.find(a => a.id === formData.activity);
                 
                 if (selectedProject?.ticket_id_prefix && selectedActivity?.requires_ticket_id) {
                   const activityName = selectedActivity.name.toLowerCase();
                   const isTicketResolution = activityName.includes('ticket resolution') || 
                                             activityName.includes('am') || 
                                             activityName.includes('enh');
                   
                   if (isTicketResolution) {
                     const prefix = selectedProject.ticket_id_prefix;
                     const selectionStart = e.target.selectionStart;
                     const selectionEnd = e.target.selectionEnd;
                     
                     // Se la selezione include parte del prefix, sposta la selezione dopo il prefix
                     if (selectionStart < prefix.length) {
                       e.target.setSelectionRange(prefix.length, Math.max(selectionEnd, prefix.length));
                     }
                   }
                 }
               }}
               required={formData.activity && activities.find(a => a.id === formData.activity)?.requires_ticket_id}
               sx={{ width: '100%' }}
              helperText={(() => {
                const selectedProject = projects.find(p => p.id === formData.project);
                const selectedActivity = activities.find(a => a.id === formData.activity);
                if (!selectedActivity) return '';
                const activityName = selectedActivity.name?.toLowerCase?.() || '';
                const isTicketResolution = activityName.includes('ticket resolution') || activityName.includes('am') || activityName.includes('enh');
                const parts = [];
                if (selectedProject?.ticket_id_prefix && isTicketResolution) {
                  parts.push(`Prefix "${selectedProject.ticket_id_prefix}" is locked.`);
                }
                if (isTicketResolution && (selectedProject?.regex || selectedProject?.regex_desc)) {
                  if (selectedProject?.regex_desc) {
                    parts.push(selectedProject.regex_desc);
                  } else {
                    parts.push('This project enforces a specific Ticket ID format.');
                  }
                }
                return parts.join(' ');
              })()}
             />
                           <TextField
                name="effort"
                label="Effort"
                value={formData.effort}
                onChange={handleFormChange}
                required
                placeholder="e.g., 30, 60, 120"
                InputProps={{
                  endAdornment: <InputAdornment position="end">min</InputAdornment>,
                }}
                sx={{ width: '100%' }}
              />
            <TextField
               name="description"
               label="Description"
               value={formData.description}
               onChange={handleFormChange}
               multiline
               rows={2}
              required={formData.activity && activities.find(a => a.id === formData.activity)?.requires_description}
               sx={{ width: '100%' }}
             />
             
             {/* Avviso per restrizioni temporali del progetto */}
             {formData.project && editingRow && isDateBlockedForProject(editingRow.date, formData.project, projects) && (
               <Alert severity="warning" sx={{ mt: 2 }}>
                 {getDateRestrictionMessage(formData.project, projects, new Date(editingRow.date))}
               </Alert>
             )}
             
             <Box sx={{ 
               bgcolor: '#f5f5f5', 
               p: 1.5, 
               borderRadius: 1, 
               border: '1px solid #e0e0e0',
               fontSize: '0.875rem',
               color: '#666'
             }}>
               <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                Note: Effort input must be EXCLUSIVELY in minutes (enter only the number, e.g., 30, 60, 120).
              </Typography>
             </Box>
            {formError && <Alert severity="error">{formError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSaveWorklog} 
            variant="contained" 
            color="primary" 
            disabled={saving || (formData.project && editingRow && isDateBlockedForProject(editingRow.date, formData.project, projects))}
          >
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}